import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { 
  FaChartBar, FaFilter, FaDownload, FaInfoCircle, FaExclamationTriangle, 
  FaRobot, FaLayerGroup, FaMicrochip, FaList, FaClock, FaSearch, FaCheckCircle, FaArrowLeft 
} from 'react-icons/fa';
import aiModelApi from '../../services/aiModelApi';
import * as api from '../../services/api';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const ModelDashboard = ({ setError, setSuccess }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Specific state for drilling down
  const [selectedMicroAgent, setSelectedMicroAgent] = useState(null);
  
  // Filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    // Reset data when switching tabs to avoid stale views
    setData(null);
    if (selectedAgent || activeTab === 'overview') {
      fetchDashboardData();
    }
  }, [activeTab, selectedAgent, startDate, endDate, selectedMicroAgent]);

  // Polling for live monitoring tabs
  useEffect(() => {
    let interval;
    if (activeTab === 'pending' || activeTab === 'logs') {
      interval = setInterval(() => {
        if (selectedAgent) fetchDashboardData(true); // Silent update
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [activeTab, selectedAgent]);

  const fetchAgents = async () => {
    try {
      const response = await api.getAgents();
      let agentsList = [];
      if (response && response.agents) {
        agentsList = response.agents;
      } else if (response && response.body && response.body.agents) {
        agentsList = response.body.agents;
      }
      setAgents(agentsList);
      
      // Auto-select first agent if available
      if (agentsList.length > 0 && !selectedAgent) {
        setSelectedAgent(agentsList[0].uniqueIdentifier);
      }
    } catch (e) {
      console.error('Error fetching agents', e);
      setError('Failed to load agents: ' + e.message);
    }
  };

  const fetchDashboardData = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null); // Clear previous errors

    // Base filters without limit property as keys vary by request type
    const baseFilters = {
      start_date: startDate,
      end_date: endDate
    };

    const agentIdInt = selectedAgent ? parseInt(selectedAgent) : null;

    // Validate requirements
    if (activeTab !== 'overview' && !agentIdInt) {
      if (!silent) setLoading(false);
      return; // Wait for agent selection
    }

    try {
      let response;

      switch (activeTab) {
        case 'overview':
          response = await aiModelApi.dashboard.getAgentTotals(
            agentIdInt 
              ? { agent_id: agentIdInt, ...baseFilters, limit_per_micro_agent: limit } 
              : { ...baseFilters, limit_per_micro_agent: limit }
          );
          break;
        case 'mini_agents':
          response = await aiModelApi.dashboard.getMiniAgentBreakdown(agentIdInt, { 
            ...baseFilters, 
            limit_per_micro_agent: limit 
          });
          break;
        case 'micro_agents':
          response = await aiModelApi.dashboard.getMicroAgentBreakdown(agentIdInt, { 
            ...baseFilters, 
            limit_per_micro_agent: limit 
          });
          break;
        case 'models':
          response = await aiModelApi.dashboard.getModelBreakdown(agentIdInt, { 
            ...baseFilters, 
            limit_llm_calls: limit 
          });
          break;
        case 'levels':
          response = await aiModelApi.dashboard.getLevelBreakdown(agentIdInt, { 
            ...baseFilters, 
            limit_llm_calls: limit 
          });
          break;
        case 'details':
          if (selectedMicroAgent) {
            response = await aiModelApi.dashboard.getMicroAgentDetails(selectedMicroAgent.id, { 
              ...baseFilters, 
              limit_executions: limit 
            });
          }
          break;
        case 'pending':
          response = await aiModelApi.dashboard.getPendingCalls(agentIdInt);
          break;
        case 'logs':
          response = await aiModelApi.dashboard.getLlmCallLogs(agentIdInt, { 
            ...baseFilters, 
            limit: limit 
          });
          break;
        default:
          break;
      }

      if (response && response.success) {
        // Robust data handling: Check if 'data' property exists and contains expected sub-properties
        // If not, maybe the response itself is the data object (flattened response)
        if (response.data && (response.data.levels || response.data.models || response.data.totals || response.data.mini_agents || response.data.micro_agents || response.data.llm_calls)) {
          setData(response.data);
        } else {
          // If response.data doesn't contain the expected arrays, assume the response root has them
          // This handles cases where API might return flattened { success: true, levels: [...] } 
          // OR if response.data IS the array itself (though unlikely based on schema)
          setData(response);
        }
      } else if (response && !response.success && response.error) {
        if (!silent) setError(response.error);
      }
    } catch (err) {
      console.error('Dashboard error:', err);
      if (!silent) setError("An unexpected error occurred while loading dashboard data");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --- Render Helpers ---

  const renderOverview = () => {
    const totals = Array.isArray(data?.totals) ? data.totals : (data?.totals ? [data.totals] : (data?.data?.totals ? [data.data.totals] : []));
    
    if (totals.length === 0) return <NoDataMessage />;

    const chartLabels = totals.map(t => t.agent_name || `Agent ${t.agent_id}`);
    const costData = totals.map(t => parseFloat(t.total_cost || 0));
    const callsData = totals.map(t => parseInt(t.total_llm_calls || 0));

    const chartData = {
      labels: chartLabels,
      datasets: [
        {
          label: 'Total Cost ($)',
          data: costData,
          backgroundColor: 'rgba(0, 229, 255, 0.6)',
          borderColor: 'rgba(0, 229, 255, 1)',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          label: 'Total Calls',
          data: callsData,
          backgroundColor: 'rgba(41, 121, 255, 0.6)',
          borderColor: 'rgba(41, 121, 255, 1)',
          borderWidth: 1,
          yAxisID: 'y1',
        }
      ]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: '#fff' } },
        title: { display: true, text: 'Agent Usage Overview', color: '#fff', font: { size: 16 } }
      },
      scales: {
        x: { ticks: { color: '#fff' }, grid: { color: 'rgba(255, 255, 255, 0.1)' } },
        y: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Cost ($)', color: '#fff' }, ticks: { color: '#fff' }, grid: { color: 'rgba(255, 255, 255, 0.1)' } },
        y1: { type: 'linear', display: true, position: 'right', title: { display: true, text: 'Calls', color: '#fff' }, ticks: { color: '#fff' }, grid: { drawOnChartArea: false } }
      }
    };

    return (
      <>
        <Row className="mb-4">
          <Col md={12} lg={selectedAgent ? 12 : 8}>
            <div className="chart-container" style={{ height: '350px' }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          </Col>
          {!selectedAgent && (
            <Col md={12} lg={4}>
              <div className="metric-card h-100 d-flex flex-column justify-content-center align-items-center text-center p-4">
                <h2 className="display-4 text-info fw-bold">{totals.length}</h2>
                <div className="text-muted text-uppercase letter-spacing-1">Active Agents</div>
              </div>
            </Col>
          )}
        </Row>
        
        <div className="dashboard-metrics-grid">
          {totals.map((t, index) => (
            <div key={t.agent_id || index} className="metric-card">
              <h5 className="text-white mb-3 text-truncate" title={t.agent_name}>{t.agent_name || `Agent ${t.agent_id}`}</h5>
              <Row className="g-0 mb-3">
                <Col xs={6}>
                  <div className="metric-value text-cyan">${parseFloat(t.total_cost || 0).toFixed(2)}</div>
                  <div className="metric-label">Total Cost</div>
                </Col>
                <Col xs={6}>
                  <div className="metric-value text-blue">{parseInt(t.total_llm_calls || 0).toLocaleString()}</div>
                  <div className="metric-label">LLM Calls</div>
                </Col>
              </Row>
              <div className="d-flex justify-content-between align-items-center pt-3 border-top border-secondary">
                <div>
                  <div className="small text-muted">Success Rate</div>
                  <div className={`fw-bold ${parseFloat(t.success_rate) > 95 ? 'text-success' : 'text-warning'}`}>
                    {parseFloat(t.success_rate || 0).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="small text-muted">Tokens</div>
                  <div className="fw-bold text-white">
                    {((parseInt(t.total_input_tokens || 0) + parseInt(t.total_output_tokens || 0)) / 1000).toFixed(1)}k
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderMiniAgentBreakdown = () => {
    const miniAgents = data?.mini_agents || data?.data?.mini_agents || [];
    if (miniAgents.length === 0) return <NoDataMessage />;

    return (
      <div className="table-responsive">
        <Table className="ai-table" hover>
          <thead>
            <tr>
              <th>Mini Agent</th>
              <th>Executions</th>
              <th>Total Calls</th>
              <th>Total Cost</th>
              <th>Cost/Exec</th>
              <th>Tokens In/Out</th>
              <th>Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {miniAgents.map((ma, idx) => (
              <tr key={ma.micro_agent_id || idx}>
                <td>
                  <div className="fw-bold text-white">{ma.micro_agent_name}</div>
                  <small className="text-muted">#{ma.micro_agent_number}</small>
                </td>
                <td>{ma.execution_count}</td>
                <td>{ma.total_llm_calls}</td>
                <td className="cost-display">${parseFloat(ma.total_cost).toFixed(3)}</td>
                <td className="cost-display">${parseFloat(ma.cost_per_execution).toFixed(3)}</td>
                <td>
                  <div className="small">
                    <div>In: {parseInt(ma.total_input_tokens).toLocaleString()}</div>
                    <div>Out: {parseInt(ma.total_output_tokens).toLocaleString()}</div>
                  </div>
                </td>
                <td><SuccessRateBadge rate={ma.success_rate} /></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderMicroAgentBreakdown = () => {
    const microAgents = data?.micro_agents || data?.data?.micro_agents || [];
    if (microAgents.length === 0) return <NoDataMessage />;

    return (
      <div className="table-responsive">
        <Table className="ai-table" hover>
          <thead>
            <tr>
              <th>Micro Agent</th>
              <th>Type</th>
              <th>Executions</th>
              <th>Calls</th>
              <th>Cost</th>
              <th>Avg Cost</th>
              <th>Success Rate</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {microAgents.map((ma, idx) => (
              <tr key={ma.micro_agent_id || idx}>
                <td>
                  <div className="fw-bold text-white">{ma.micro_agent_name}</div>
                  <small className="text-muted">#{ma.micro_agent_number}</small>
                </td>
                <td>
                  {ma.is_mini_agent ? 
                    <Badge bg="info">Mini</Badge> : 
                    <Badge bg="secondary">Micro</Badge>
                  }
                </td>
                <td>{ma.execution_count}</td>
                <td>{ma.total_llm_calls}</td>
                <td className="cost-display">${parseFloat(ma.total_cost).toFixed(3)}</td>
                <td className="cost-display">${parseFloat(ma.cost_per_execution).toFixed(3)}</td>
                <td><SuccessRateBadge rate={ma.success_rate} /></td>
                <td>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => {
                      setSelectedMicroAgent({ id: ma.micro_agent_id, name: ma.micro_agent_name });
                      setActiveTab('details');
                    }}
                  >
                    <FaSearch /> Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderModelBreakdown = () => {
    const models = data?.models || data?.data?.models || [];
    if (models.length === 0) return <NoDataMessage />;

    return (
      <div className="table-responsive">
        <Table className="ai-table" hover>
          <thead>
            <tr>
              <th>Model</th>
              <th>Provider</th>
              <th>Calls</th>
              <th>Cost</th>
              <th>Avg Cost/Call</th>
              <th>Avg Tokens/Call</th>
              <th>Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m, idx) => (
              <tr key={m.llm_key || idx}>
                <td className="fw-bold text-white">{m.model_name}</td>
                <td><Badge bg="secondary">{m.provider}</Badge></td>
                <td>{m.total_llm_calls}</td>
                <td className="cost-display">${parseFloat(m.total_cost).toFixed(3)}</td>
                <td className="cost-display">${parseFloat(m.cost_per_call).toFixed(4)}</td>
                <td>
                  <div className="small">
                    <div>In: {parseFloat(m.input_tokens_per_call).toFixed(0)}</div>
                    <div>Out: {parseFloat(m.output_tokens_per_call).toFixed(0)}</div>
                  </div>
                </td>
                <td><SuccessRateBadge rate={m.success_rate} /></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderLevelBreakdown = () => {
    // Robust checking for data structure
    const levels = data?.levels || data?.data?.levels || [];
    
    if (!levels || levels.length === 0) return <NoDataMessage />;

    return (
      <div className="table-responsive">
        <Table className="ai-table" hover>
          <thead>
            <tr>
              <th>Level</th>
              <th>Category</th>
              <th>Calls</th>
              <th>Total Cost</th>
              <th>Cost/Call</th>
              <th>Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {levels.map((l, idx) => (
              <tr key={l.llm_level || idx}>
                <td><Badge bg="primary" className="fs-6">Level {l.llm_level}</Badge></td>
                <td>
                  <div className="text-white">{l.category}</div>
                  <small className="text-muted">{l.sub_category}</small>
                </td>
                <td>{l.total_llm_calls}</td>
                <td className="cost-display">${parseFloat(l.total_cost).toFixed(3)}</td>
                <td className="cost-display">${parseFloat(l.cost_per_call).toFixed(4)}</td>
                <td><SuccessRateBadge rate={l.success_rate} /></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderMicroAgentDetails = () => {
    if (!selectedMicroAgent) {
      return (
        <Alert variant="info" className="text-center">
          <FaInfoCircle className="me-2" />
          Please select a micro-agent from the "Micro-Agents" tab to view details.
        </Alert>
      );
    }

    if (!data) return <div className="text-center p-3">Loading details...</div>;

    const { by_model, by_level } = data;

    return (
      <div>
        <div className="d-flex align-items-center mb-4">
          <Button variant="outline-secondary" size="sm" className="me-3" onClick={() => setActiveTab('micro_agents')}>
            <FaArrowLeft /> Back
          </Button>
          <h4 className="mb-0 text-white">Details for: {selectedMicroAgent.name}</h4>
        </div>

        <Row>
          <Col md={12} className="mb-4">
            <Card className="ai-card">
              <div className="ai-card-header">
                <h5 className="ai-card-title"><FaRobot /> By Model</h5>
              </div>
              <div className="ai-card-body">
                <Table className="ai-table" size="sm" hover>
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th>Executions</th>
                      <th>Calls</th>
                      <th>Calls/Exec</th>
                      <th>Cost/Call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {by_model?.details?.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.model_name} <small className="text-muted">({item.provider})</small></td>
                        <td>{item.total_executions}</td>
                        <td>{item.total_llm_calls}</td>
                        <td>{parseFloat(item.llm_calls_per_execution).toFixed(1)}</td>
                        <td className="cost-display">${parseFloat(item.cost_per_call).toFixed(4)}</td>
                      </tr>
                    ))}
                    {by_model?.grand_total && (
                      <tr className="fw-bold border-top border-light">
                        <td>Total</td>
                        <td>{by_model.grand_total.total_executions}</td>
                        <td>{by_model.grand_total.total_llm_calls}</td>
                        <td>{parseFloat(by_model.grand_total.avg_llm_calls_per_execution).toFixed(1)}</td>
                        <td className="cost-display">${parseFloat(by_model.grand_total.avg_cost_per_call).toFixed(4)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card>
          </Col>
          <Col md={12}>
            <Card className="ai-card">
              <div className="ai-card-header">
                <h5 className="ai-card-title"><FaLayerGroup /> By Level</h5>
              </div>
              <div className="ai-card-body">
                <Table className="ai-table" size="sm" hover>
                  <thead>
                    <tr>
                      <th>Level</th>
                      <th>Executions</th>
                      <th>Calls</th>
                      <th>Calls/Exec</th>
                      <th>Cost/Call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {by_level?.details?.map((item, idx) => (
                      <tr key={idx}>
                        <td>Level {item.llm_level} <small className="text-muted">({item.category})</small></td>
                        <td>{item.total_executions}</td>
                        <td>{item.total_llm_calls}</td>
                        <td>{parseFloat(item.llm_calls_per_execution).toFixed(1)}</td>
                        <td className="cost-display">${parseFloat(item.cost_per_call).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const renderPendingCalls = () => {
    const calls = data?.pending_calls || data?.data?.pending_calls || [];
    
    if (calls.length === 0) {
      return (
        <div className="text-center p-5">
          <FaCheckCircle className="text-success mb-3" size={48} />
          <h5 className="text-white">No Pending Calls</h5>
          <p className="text-muted">All systems are operational.</p>
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <Table className="ai-table" hover>
          <thead>
            <tr>
              <th>Time</th>
              <th>Duration</th>
              <th>Micro-Agent</th>
              <th>Function</th>
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((call, idx) => (
              <tr key={call.llm_call_id || idx}>
                <td>{new Date(call.invoked_time).toLocaleString()}</td>
                <td>
                  <Badge bg={call.seconds_pending > 60 ? 'danger' : 'warning'}>
                    {parseFloat(call.seconds_pending).toFixed(1)}s
                  </Badge>
                </td>
                <td>{call.micro_agent_name} <small className="text-muted">#{call.micro_agent_number}</small></td>
                <td>{call.invoke_function_name}</td>
                <td className="small text-muted">{call.correlation_id}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderLogs = () => {
    const logs = data?.llm_calls || data?.data?.llm_calls || [];
    if (logs.length === 0) return <NoDataMessage />;

    return (
      <div className="table-responsive">
        <Table className="ai-table" size="sm" hover>
          <thead>
            <tr>
              <th>Time</th>
              <th>Agent/Func</th>
              <th>Model/Level</th>
              <th>Tokens</th>
              <th>Status</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((call, idx) => (
              <tr key={call.llm_call_id || idx}>
                <td>
                  <div className="small text-white">{new Date(call.start_time).toLocaleTimeString()}</div>
                  <div className="super-small text-muted">{new Date(call.start_time).toLocaleDateString()}</div>
                </td>
                <td>
                  <div className="fw-bold text-white">{call.micro_agent_name}</div>
                  <div className="small text-muted">{call.invoke_function_name}</div>
                </td>
                <td>
                  <div className="small">{call.model_name}</div>
                  <Badge bg="dark" className="border border-secondary">L{call.llm_level}</Badge>
                  {call.reasoning && <Badge bg="info" className="ms-1">Reasoning</Badge>}
                </td>
                <td>
                  <div className="small">In: {call.input_tokens}</div>
                  <div className="small">Out: {call.output_tokens}</div>
                </td>
                <td>
                  <StatusBadge status={call.status} error={call.error_type} />
                  {call.duration_seconds !== null && call.duration_seconds !== undefined && (
                    <div className="super-small text-muted mt-1">
                      {parseFloat(call.duration_seconds).toFixed(2)}s
                    </div>
                  )}
                </td>
                <td className="cost-display">${parseFloat(call.call_cost).toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // --- Components ---

  const NoDataMessage = () => (
    <Alert variant="warning" className="text-center">
      <FaExclamationTriangle className="me-2" />
      No data found for the selected filters.
    </Alert>
  );

  const SuccessRateBadge = ({ rate }) => {
    const r = parseFloat(rate || 0);
    let variant = 'success';
    if (r < 80) variant = 'danger';
    else if (r < 95) variant = 'warning';
    
    return <Badge bg={variant}>{r.toFixed(1)}%</Badge>;
  };

  const StatusBadge = ({ status, error }) => {
    if (status === 'completed') return <Badge bg="success">Success</Badge>;
    if (status === 'failed') return <Badge bg="danger" title={error}>Failed</Badge>;
    if (status === 'pending') return <Badge bg="warning">Pending</Badge>;
    return <Badge bg="secondary">{status}</Badge>;
  };

  return (
    <div className="model-dashboard">
      {/* REDESIGNED: Horizontal View Navigation Tabs - Positioned Above Filters */}
      <div className="dashboard-view-tabs-container">
        <div className="dashboard-view-tabs">
          <div className="view-tab-nav">
            <button
              className={`view-tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <FaChartBar className="tab-icon" />
              <span className="tab-text">Overview</span>
            </button>
            <button
              className={`view-tab-button ${activeTab === 'mini_agents' ? 'active' : ''}`}
              onClick={() => setActiveTab('mini_agents')}
            >
              <FaRobot className="tab-icon" />
              <span className="tab-text">Mini-Agents</span>
            </button>
            <button
              className={`view-tab-button ${activeTab === 'micro_agents' ? 'active' : ''}`}
              onClick={() => setActiveTab('micro_agents')}
            >
              <FaMicrochip className="tab-icon" />
              <span className="tab-text">Micro-Agents</span>
            </button>
            <button
              className={`view-tab-button ${activeTab === 'models' ? 'active' : ''}`}
              onClick={() => setActiveTab('models')}
            >
              <FaLayerGroup className="tab-icon" />
              <span className="tab-text">Models</span>
            </button>
            <button
              className={`view-tab-button ${activeTab === 'levels' ? 'active' : ''}`}
              onClick={() => setActiveTab('levels')}
            >
              <FaList className="tab-icon" />
              <span className="tab-text">Levels</span>
            </button>
            {selectedMicroAgent && (
              <button
                className={`view-tab-button ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                <FaSearch className="tab-icon" />
                <span className="tab-text">Details</span>
              </button>
            )}
            <button
              className={`view-tab-button ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              <FaClock className="tab-icon" />
              <span className="tab-text">Pending</span>
            </button>
            <button
              className={`view-tab-button ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              <FaList className="tab-icon" />
              <span className="tab-text">Logs</span>
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Horizontal Filters - Now Positioned Below View Tabs */}
      <div className="dashboard-filters-container">
        <div className="filters-row">
          <div className="filter-group">
            <label className="filter-label">Agent Context</label>
            <Form.Select 
              value={selectedAgent} 
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="filter-control"
            >
              <option value="">All Agents</option>
              {agents.map(a => (
                <option key={a.uniqueIdentifier} value={a.uniqueIdentifier}>
                  {a.agentName || a.repoName}
                </option>
              ))}
            </Form.Select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Start Date</label>
            <Form.Control 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-control"
            />
          </div>
          
          <div className="filter-group">
            <label className="filter-label">End Date</label>
            <Form.Control 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-control"
            />
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Limit</label>
            <Form.Control 
              type="number" 
              value={limit} 
              onChange={(e) => setLimit(parseInt(e.target.value) || 100)} 
              min="1" 
              max="10000"
              className="filter-control"
            />
          </div>
          
          <div className="filter-group">
            <div className="filter-actions">
              <Button 
                variant="primary" 
                onClick={() => fetchDashboardData()} 
                disabled={loading}
                className="flex-grow-1"
              >
                {loading ? <Spinner size="sm" className="me-2"/> : <FaFilter className="me-2" />}
                Apply Filters
              </Button>
              <Button 
                variant="outline-secondary" 
                disabled={!data}
                title="Export Data"
              >
                <FaDownload />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <Card className="ai-card">
        <div className="ai-card-header">
          <h3 className="ai-card-title">
            <FaChartBar />
            Dashboard Analytics
          </h3>
          
          <div className="text-muted small">
            {data && <Badge bg="dark" className="border border-secondary">{startDate} to {endDate}</Badge>}
          </div>
        </div>
        
        <div className="ai-card-body">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading dashboard data...</p>
            </div>
          ) : !selectedAgent && activeTab !== 'overview' ? (
            <Alert variant="info" className="text-center">
              <FaInfoCircle className="me-2" /> Please select a specific Agent to view detailed breakdowns.
            </Alert>
          ) : (
            <>
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'mini_agents' && renderMiniAgentBreakdown()}
              {activeTab === 'micro_agents' && renderMicroAgentBreakdown()}
              {activeTab === 'models' && renderModelBreakdown()}
              {activeTab === 'levels' && renderLevelBreakdown()}
              {activeTab === 'details' && renderMicroAgentDetails()}
              {activeTab === 'pending' && renderPendingCalls()}
              {activeTab === 'logs' && renderLogs()}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ModelDashboard;